/**
 * 文件压缩工具
 *
 * 图片：Canvas API（客户端，保留宽高比，输出 JPEG）
 * 视频：ffmpeg.wasm（运行时动态加载，不经过 webpack，H.264 + AAC，音轨完整保留）
 *
 * 需将以下文件放在 public/ffmpeg/ 目录下：
 *   ffmpeg.min.js / ffmpeg-core.js / ffmpeg-core.wasm / ffmpeg-core.worker.js
 */

// ─── 常量 ──────────────────────────────────────────────────────────────────

const IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/bmp']
const VIDEO_TYPES = [
  'video/mp4', 'video/webm', 'video/ogg', 'video/quicktime',
  'video/x-msvideo', 'video/x-matroska', 'video/x-flv', 'video/3gpp'
]

// ─── ffmpeg 动态加载（绕过 webpack，直接用 script 标签） ────────────────────

let _ffmpeg = null
let _scriptLoaded = false

/**
 * 动态插入 script 标签加载 ffmpeg.min.js，只执行一次
 */
function loadFFmpegScript() {
  if (_scriptLoaded || window.FFmpeg) {
    _scriptLoaded = true
    return Promise.resolve()
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = '/ffmpeg/ffmpeg.min.js'
    script.onload = () => { _scriptLoaded = true; resolve() }
    script.onerror = () => reject(new Error('ffmpeg.min.js 加载失败'))
    document.head.appendChild(script)
  })
}

/**
 * 获取 ffmpeg 实例（单例，懒加载）
 */
async function getFFmpeg() {
  await loadFFmpegScript()

  if (!window.FFmpeg) throw new Error('FFmpeg 未挂载到 window')

  const { createFFmpeg } = window.FFmpeg

  if (!_ffmpeg) {
    _ffmpeg = createFFmpeg({
      corePath: `${window.location.origin}/ffmpeg/ffmpeg-core.js`,
      log: false
    })
  }
  if (!_ffmpeg.isLoaded()) {
    await _ffmpeg.load()
  }
  return _ffmpeg
}

// ─── 图片压缩 ───────────────────────────────────────────────────────────────

/**
 * 压缩图片（Canvas API）
 * @param {File} file
 * @param {object} [opts]
 * @param {number} [opts.maxWidth=1920]
 * @param {number} [opts.maxHeight=1080]
 * @param {number} [opts.quality=0.8]    JPEG 质量 0~1
 * @returns {Promise<File>}
 */
export function compressImage(file, opts = {}) {
  const { maxWidth = 1920, maxHeight = 1080, quality = 0.8 } = opts

  return new Promise((resolve) => {
    if (!IMAGE_TYPES.includes(file.type)) return resolve(file)

    const reader = new FileReader()
    reader.onload = ({ target }) => {
      const img = new Image()
      img.onload = () => {
        let { width, height } = img

        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height)
          width = Math.round(width * ratio)
          height = Math.round(height * ratio)
        }

        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.fillStyle = '#ffffff' // 白底，防止 PNG 透明背景转 JPEG 后变黑
        ctx.fillRect(0, 0, width, height)
        ctx.drawImage(img, 0, 0, width, height)

        canvas.toBlob((blob) => {
          if (!blob || blob.size >= file.size) return resolve(file)
          const name = file.name.replace(/\.[^.]+$/, '.jpg')
          resolve(new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() }))
        }, 'image/jpeg', quality)
      }
      img.onerror = () => resolve(file)
      img.src = target.result
    }
    reader.onerror = () => resolve(file)
    reader.readAsDataURL(file)
  })
}

// ─── 视频压缩 ───────────────────────────────────────────────────────────────

/**
 * 压缩视频（ffmpeg.wasm，H.264 + AAC，音轨完整保留）
 * @param {File} file
 * @param {object} [opts]
 * @param {number}   [opts.crf=28]           质量因子 18~51，越大压缩越强
 * @param {string}   [opts.preset='fast']    编码速度：ultrafast/fast/medium/slow
 * @param {number}   [opts.audioBitrate=128] 音频码率（kbps）
 * @param {Function} [opts.onProgress]       进度回调，参数为 0~100 整数
 * @returns {Promise<File>}
 */
export async function compressVideo(file, opts = {}) {
  const { crf = 28, preset = 'fast', audioBitrate = 128, onProgress } = opts

  try {
    const ff = await getFFmpeg()
    const { fetchFile } = window.FFmpeg

    if (onProgress) {
      ff.setProgress(({ ratio }) => {
        onProgress(Math.min(100, Math.round(ratio * 100)))
      })
    }

    const ext = (file.name.match(/\.([^.]+)$/) || [])[1] || 'mp4'
    const inputName = `input.${ext}`
    const outputName = 'output.mp4'

    ff.FS('writeFile', inputName, await fetchFile(file))

    await ff.run(
      '-i', inputName,
      '-vcodec', 'libx264',
      '-crf', String(crf),
      '-preset', preset,
      '-acodec', 'aac',
      '-b:a', `${audioBitrate}k`,
      '-movflags', '+faststart',
      outputName
    )

    const data = ff.FS('readFile', outputName)
    try { ff.FS('unlink', inputName) } catch (_) {}
    try { ff.FS('unlink', outputName) } catch (_) {}

    const blob = new Blob([data.buffer], { type: 'video/mp4' })
    if (blob.size >= file.size) return file

    const name = file.name.replace(/\.[^.]+$/, '.mp4')
    return new File([blob], name, { type: 'video/mp4', lastModified: Date.now() })
  } catch (err) {
    // 把错误往上抛，让调用方决定如何处理（显示 toast 等）
    throw err
  }
}

// ─── 统一入口 ────────────────────────────────────────────────────────────────

/**
 * 根据文件类型自动选择压缩方式
 * @param {File} file
 * @param {object} [opts]
 * @returns {Promise<File>}
 */
export function compressFile(file, opts = {}) {
  if (IMAGE_TYPES.includes(file.type)) return compressImage(file, opts)
  if (VIDEO_TYPES.includes(file.type)) return compressVideo(file, opts)
  return Promise.resolve(file)
}
